import { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import config from "../../config";
import "./SignIn.css";
import logo from "../../assets/images/RedP_LogoT.png";

const REDP_AUTH_TIMEOUT_MS = Number(process.env.REACT_APP_REDP_AUTH_TIMEOUT_MS || 60000);

const fetchWithTimeout = async (url, options = {}, timeoutMessage = "Request timed out.") => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REDP_AUTH_TIMEOUT_MS);

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error(timeoutMessage);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

// --- ICONS ---
const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
    <line x1="1" y1="1" x2="23" y2="23"></line>
  </svg>
);

// --- FOOTER COMPONENT ---
const SimpleFooter = () => (
  <div className="footer-container">
    <div className="footer-links">
      <a 
        href="https://www.waterviewai.com/#/terms-and-conditions" 
        className="footer-link"
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        Terms of Service
      </a>
      <span className="separator">•</span>
      <a 
        href="https://www.waterviewai.com/#/privacy-policy" 
        className="footer-link"
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        Privacy Policy
      </a>
    </div>
  </div>
);

const SignIn = () => {
  const { setUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");

    const url =
      mode === "login"
        ? `${config.REACT_APP_API_URL}/auth/token`
        : `${config.REACT_APP_API_URL}/auth/register`;

    const formData = new FormData();
    formData.append("email", email.toLowerCase());
    formData.append("password", password);

    if (mode === "register") {
      formData.append("first_name", firstName);
      formData.append("last_name", lastName);
      formData.append("created_at", "");
      formData.append("user_type", "user");
    } else {
      formData.append("username", email.toLowerCase());
    }

    try {
      const response = await fetchWithTimeout(url, {
        method: "POST",
        body: formData,
        credentials: "include",
      }, `${mode === "login" ? "Login" : "Registration"} timed out. Please try again.`);

      const data = await response.json().catch(() => ({
        detail: "The server returned an unexpected response.",
      }));

      if (response.ok) {
        localStorage.setItem("access_token", data.access_token);

        const userRes = await fetchWithTimeout(`${config.REACT_APP_API_URL}/auth/users/me`, {
          headers: { Authorization: data.access_token },
        }, "Login succeeded, but loading your profile timed out. Please refresh and try again.");

        const userData = await userRes.json().catch(() => ({
          detail: "Unable to load your user profile.",
        }));
        if (!userRes.ok) {
          throw new Error(userData.detail || "Unable to load your user profile.");
        }
        setUser(userData);
        navigate(`/redp/${userData.id}`);
      } else {
        setMessage(`Error: ${data.detail}`);
      }
    } catch (err) {
      if (err.message === "Failed to fetch") {
        setMessage("Network error. Please try again.");
      } else {
        setMessage(`Error: ${err.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // --- CONTENT BLOCKS ---
  const loginContent = (
    <div className="info-content animate-fade-in">
      <h2>Login to RedP</h2>
      <p>Welcome back! Please remember:</p>

      <div className="product-notice-box">
        <ul>
          <li>
            <strong>Review Output:</strong> Please proofread all generated
            documents before relying on them.
          </li>
          <li>
            <strong>Feedback:</strong> Your feedback is encouraged to help us
            improve.
          </li>
          <li>
            <strong>Privacy:</strong> We do not store document data. Outputs
            exist only for your session.
          </li>
        </ul>
      </div>

      <p className="agreement-text">
        Your use of RedP implies understanding and agreement to these terms.
      </p>
    </div>
  );

  const registerContent = (
    <div className="info-content animate-fade-in">
      <h2>Welcome to RedP</h2>
      <p>
        Thank you for registering to use RedP, a real estate document
        processor built to help review and structure contract information.
      </p>
      <p>Before you proceed, please take a moment to review the following:</p>

      <div className="product-notice-box">
        <ol>
          <li>
            <strong>Review Generated Output</strong>
            <p>
              RedP automates document review workflows, but generated output
              should still be proofread carefully before you rely on it.
            </p>
          </li>
          <li>
            <strong>We Value Your Feedback</strong>
            <p>
              Your insights are invaluable to us. Please share any feedback you
              have regarding usability, workflow, document accuracy, or anything
              else that could improve the system.
            </p>
          </li>
          <li>
            <strong>Data Privacy and Session Handling</strong>
            <ul>
              <li>
                Only your first name, last name, email address, and password are
                retained to enable login and provide basic account continuity.
              </li>
              <li>
                Any documents you upload or text you enter are processed
                in-session only.
              </li>
              <li>
                No data is saved or stored beyond your login information and any
                customization settings you may choose to apply.
              </li>
              <li>
                We do not store or retain any processed content. Accordingly,
                all outputs exist only for the duration of your session and will
                be unavailable once your session has ended. If your session
                expires, you will need to reprocess your document in a new
                session.
              </li>
            </ul>
          </li>
        </ol>
      </div>

      <p className="agreement-text">
        By registering, you acknowledge and accept the above conditions. Welcome
        aboard, and thank you for using RedP.
      </p>
    </div>
  );

  return (
    <div className="auth-page-container">
      <div className="split-view-card">
        {/* Left Panel (Info) */}
        <div className="info-section">
          {mode === "login" ? loginContent : registerContent}
          <div className="info-footer-wrapper">
            <SimpleFooter />
          </div>
        </div>

        {/* Right Panel (Auth Form) */}
        <div className="auth-section">
          <div className="auth-content-wrapper">
              <img src={logo} alt="RedP Logo" className="logo" />

              <h1>{mode === "login" ? "Sign In" : "Create Account"}</h1>

              <form className="auth-form" onSubmit={handleSubmit}>
                {/* EXPANDABLE FIELDS */}
                <div className={`expandable-fields ${mode === "register" ? "open" : ""}`}>
                  <div className="input-group">
                    <input
                      type="text"
                      placeholder="First Name"
                      value={firstName}
                      required={mode === "register"}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </div>
                  <div className="input-group">
                    <input
                      type="text"
                      placeholder="Last Name"
                      value={lastName}
                      required={mode === "register"}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                </div>

                {/* Standard Fields */}
                <div className="input-group">
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                
                {/* PASSWORD FIELD WITH TOGGLE */}
                <div className="input-group password-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    className="password-input"
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button 
                    type="button" 
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>

                <button type="submit" disabled={isLoading}>
                  {isLoading
                    ? "Processing..."
                    : mode === "login"
                    ? "Sign In"
                    : "Register"}
                </button>
              </form>

              {message && <div className="error-message">{message}</div>}

              <div className="toggle-link">
                {mode === "login" ? "New to RedP?" : "Have an account?"}
                <span
                  onClick={() => {
                    setMessage("");
                    setMode(mode === "login" ? "register" : "login");
                  }}
                >
                  {mode === "login" ? "Register now" : "Sign in"}
                </span>
              </div>

              {mode === "login" && (
                <div className="toggle-link" style={{ marginTop: "0.5rem" }}>
                  <span 
                    onClick={() => navigate("/forgot-password")}
                    style={{ marginLeft: 0 }} 
                  >
                    Forgot Password?
                  </span>
                </div>
              )}
          </div>
        </div>
      </div>

      <section className="tools-coming-soon" aria-labelledby="tools-coming-soon-title">
        <div className="tools-coming-soon-copy">
          <span className="tools-eyebrow">Now Available: Tools</span>
          <h2 id="tools-coming-soon-title">Meet Big 3</h2>
          <p>
            Automatically collects property documents from the Property
            Appraiser, Tax Collector, and Clerk of Courts.
          </p>
          <div className="tools-source-list" aria-label="Big 3 document sources">
            <span>Property Appraiser</span>
            <span>Tax Collector</span>
            <span>Clerk of Courts</span>
          </div>
        </div>
        <Link className="tools-learn-more" to="/downloads">
          Explore Big 3
          <span aria-hidden="true">→</span>
        </Link>
      </section>
    </div>
  );
};

export default SignIn;
